import React, { useState, useCallback } from 'react';
import { Button, Table, FormGroup, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import NumericInput from '../common/forms/NumericInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faEdit, faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { SmallButton } from '../common/forms/FormElements';
import { v4 as uuid } from 'uuid';
import { YesNoButtons } from '../common/YesNoButtons';

class BumpRule {
    id = uuid();
    editing = false;
    bumpHours = 0;
    appliesToCancellation = false;
}

export const ContractBumpRuleGrid = ({
    bumpRules: initialBumpRules,
    onBumpRulesChange,
}) => {
    const [bumpRules, setBumpRules] = useState(initialBumpRules || []);
    const [showModal, setShowModal] = useState(false);
    const [newBumpRule, setNewBumpRule] = useState(new BumpRule());

    const handleSubmit = useCallback(() => {
        const updatedBumpRules = [...bumpRules, { ...newBumpRule, editing: false }];

        setBumpRules(updatedBumpRules);
        setNewBumpRule(new BumpRule()); // Reset
        setShowModal(false); // Close modal

        onBumpRulesChange(updatedBumpRules);
    }, [bumpRules, newBumpRule, onBumpRulesChange]);

    const handleEditBumpRuleButtonClick = useCallback((id) => {
        const updated = [...bumpRules];
        const index = updated.findIndex(rule => rule.id === id);

        updated[index].editing = true;

        setBumpRules(updated);
    }, [bumpRules]);

    const handleRemoveBumpRuleButtonClick = useCallback((id) => {
        const updated = bumpRules.filter((rule) => rule.id !== id);

        setBumpRules(updated);
        onBumpRulesChange(updated);
    }, [bumpRules, onBumpRulesChange]);

    const handleUpdateBumpRule = useCallback((id, field, value) => {
        const updated = [...bumpRules];
        const index = updated.findIndex(rule => rule.id === id);

        updated[index][field] = value;

        setBumpRules(updated);
    }, [bumpRules]);

    return (
        <div>
            <div className="d-flex justify-content-end mb-1">
                <SmallButton
                    type="button"
                    onClick={() => setShowModal(true)}
                >
                    <FontAwesomeIcon icon={faPlusCircle} size="md" className="mr-2" />
                    Add Bump Rule
                </SmallButton>
            </div>

            <Table className="table table-sm bg-white" bordered responsive>
                <thead>
                    <tr>
                        <th># Hours</th>
                        <th>
                            Applies to Cancellations
                        </th>
                        <th className="text-center"><FontAwesomeIcon icon={faEdit} /></th>
                        <th className="text-center"><FontAwesomeIcon icon={faTrash} /></th>
                    </tr>
                </thead>
                <tbody>
                    {bumpRules.length === 0 ? (
                        <tr>
                            <td colSpan={5} className="text-center">
                                No bump rules found
                            </td>
                        </tr>
                    ) : (
                        bumpRules.map((bumpRule) => (
                            <tr key={bumpRule.id}>
                                {bumpRule.editing ? (
                                    <td>
                                        <NumericInput
                                            className="text-right"
                                            name="bumpHours"
                                            required
                                            value={bumpRule.bumpHours}
                                            onChange={(e) =>
                                                handleUpdateBumpRule(bumpRule.id, 'bumpHours', parseFloat(e.target.value))
                                            }
                                            min={0}
                                            step={1}
                                            wholeNumber
                                        />
                                    </td>
                                ) : (
                                    <td>
                                        {bumpRule.bumpHours}

                                        {' '}

                                        hrs
                                    </td>
                                )}

                                <td>
                                    {Boolean(bumpRule.appliesToCancellation) && (
                                        <FontAwesomeIcon icon={faCheck} />
                                    )}
                                </td>

                                <td className="text-center" style={{ width: '30px' }}>
                                    <Button
                                        type="button"
                                        color="outline-primary"
                                        size="sm"
                                        onClick={() => handleEditBumpRuleButtonClick(bumpRule.id)}
                                    >
                                        Edit
                                    </Button>
                                </td>
                                <td className="text-center" style={{ width: '30px' }}>
                                    <Button
                                        type="button"
                                        color="outline-primary"
                                        onClick={() => handleRemoveBumpRuleButtonClick(bumpRule.id)}
                                        size="sm"
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </td>
                            </tr>
                        )))}
                </tbody>
            </Table>

            <Modal isOpen={showModal} toggle={() => setShowModal(false)}>
                <ModalHeader toggle={() => setShowModal(false)}>
                    Add Bump Rule
                </ModalHeader>

                <ModalBody>
                    <NumericInput
                        label="Hours to bump to"
                        id="newBumpHours"
                        name="bumpHours"
                        value={newBumpRule.bumpHours}
                        onChange={(e) => setNewBumpRule({ ...newBumpRule, bumpHours: parseFloat(e.target.value) })}
                        min={0}
                        step={1}
                        wholeNumber
                        required
                        error="Hours to bump to is required"
                    />
                    <FormGroup>
                        <YesNoButtons
                            label="Applies to Cancellations (optional)"
                            name="appliesToCancellation"
                            value={newBumpRule.appliesToCancellation}
                            onClick={(value) => setNewBumpRule({ ...newBumpRule, appliesToCancellation: value })}
                        />
                    </FormGroup>
                </ModalBody>

                <ModalFooter>
                    <Button color="primary" onClick={handleSubmit} className="mr-1">
                        Save
                    </Button>

                    <Button color="secondary" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};
